// Empotage rapport PDF — generates the document the OT operator hands
// out instead of the (now-removed) manual rapport-d'empotage upload.
//
// Visual style mirrors features/milling/lib/usinage-report-pdf.ts so
// both rapports look like one document family: greyscale palette, A4
// portrait, neutral typography. Photos (if any) follow on subsequent
// pages — best-effort fetch via the OT's presigned-download endpoint;
// failed fetches are skipped silently rather than killing the export.

import jsPDF from "jspdf"
import autoTable from "jspdf-autotable"
import type { TFunction } from "i18next"

import { getDownloadUrl } from "@/features/transit/api/transit.api"
import type {
  EmpotageRapport,
  RapportContainerRow,
} from "@/features/transit/lib/empotage-rapport"

// Greyscale palette — kept identical to the milling rapport so both
// documents render as one consistent family.
const BORDER: [number, number, number] = [209, 213, 219] // gray-300
const SECTION_BAND: [number, number, number] = [243, 244, 246] // gray-100
const HEADER_BAND: [number, number, number] = [249, 250, 251] // gray-50
const TOTAL_BAND: [number, number, number] = [229, 231, 235] // gray-200
const TEXT_PRIMARY = 30
const TEXT_MUTED = 107

export async function downloadEmpotageRapportPdf(
  rapport: EmpotageRapport,
  otId: number | string,
  t: TFunction<"transit">,
): Promise<void> {
  const doc = new jsPDF({ unit: "pt", format: "a4", orientation: "portrait" })
  const margin = 36

  drawTitle(
    doc,
    t("empotage.rapportPdf.title", {
      otNumber: rapport.otNumber,
      defaultValue: "Rapport d'empotage — OT {{otNumber}}",
    }),
    margin,
  )
  let y = margin + 38
  y = drawHeaderMeta(doc, rapport, t, margin, y)
  y += 12

  y = drawSectionHeader(doc, t("empotage.rapportPdf.containers"), margin, y)
  y = drawContainersTable(doc, rapport, t, margin, y)
  y += 12

  y = drawSectionHeader(doc, t("empotage.rapportPdf.rollups"), margin, y)
  y = drawRollupsTable(doc, rapport, t, margin, y)

  // Observations: only render the section if at least one container
  // carries one. Saves a half-empty band on most reports.
  const observed = rapport.containers.filter((c) => !!c.observation?.trim())
  if (observed.length > 0) {
    y += 12
    y = drawSectionHeader(
      doc,
      t("empotage.rapportPdf.observations"),
      margin,
      y,
    )
    drawObservationsTable(doc, observed, t, margin, y)
  }

  // Photos — fetched concurrently. Each one gets its own page so
  // they're printable as evidence. Skip silently on per-photo fetch
  // failure so one 404 doesn't kill the whole export.
  if (rapport.photos.length > 0) {
    const dataUrls = await Promise.all(
      rapport.photos.map((p) =>
        fetchPhotoAsDataURL(otId, p.storedKey).catch(() => null),
      ),
    )
    let pageNumber = 1
    for (let i = 0; i < dataUrls.length; i++) {
      const dataUrl = dataUrls[i]
      if (!dataUrl) continue
      doc.addPage("a4", "portrait")
      drawTitle(
        doc,
        t("empotage.rapportPdf.photoPage", {
          n: pageNumber,
          total: rapport.photos.length,
          name: rapport.photos[i].name,
          defaultValue: "Photo {{n}} — {{name}}",
        }),
        margin,
      )
      drawPhoto(doc, dataUrl, margin)
      pageNumber += 1
    }
  }

  // Stamp footers on every page once we know the total.
  const total = doc.getNumberOfPages()
  for (let i = 1; i <= total; i++) {
    doc.setPage(i)
    drawFooter(doc, i, total, t)
  }

  doc.save(`${rapport.otNumber}-rapport-empotage.pdf`)
}

// ── Layout primitives ───────────────────────────────────────────────

function drawTitle(doc: jsPDF, title: string, margin: number) {
  const pageWidth = doc.internal.pageSize.getWidth()
  doc.setFontSize(13)
  doc.setFont("helvetica", "bold")
  doc.setTextColor(TEXT_PRIMARY)
  doc.text(title, pageWidth / 2, margin + 10, { align: "center" })
}

function drawHeaderMeta(
  doc: jsPDF,
  rapport: EmpotageRapport,
  t: TFunction<"transit">,
  margin: number,
  y: number,
): number {
  doc.setFontSize(9)
  doc.setFont("helvetica", "normal")
  doc.setTextColor(TEXT_MUTED)
  const cols = [
    `${t("empotage.rapportPdf.client")}: ${rapport.clientName}`,
    `${t("empotage.rapportPdf.contract")}: ${rapport.contractNumber}`,
    rapport.vessel ? `${t("empotage.rapportPdf.vessel")}: ${rapport.vessel}` : null,
    rapport.voyage ? `${t("empotage.rapportPdf.voyage")}: ${rapport.voyage}` : null,
  ].filter(Boolean) as string[]
  doc.text(cols.join("   ·   "), margin, y)
  doc.setTextColor(TEXT_PRIMARY)
  return y + 10
}

function drawSectionHeader(
  doc: jsPDF,
  label: string,
  margin: number,
  y: number,
): number {
  const pageWidth = doc.internal.pageSize.getWidth()
  const usable = pageWidth - margin * 2
  const h = 18
  doc.setFillColor(...SECTION_BAND)
  doc.setDrawColor(...BORDER)
  doc.setLineWidth(0.5)
  doc.rect(margin, y, usable, h, "FD")
  doc.setFontSize(10)
  doc.setFont("helvetica", "bold")
  doc.setTextColor(TEXT_PRIMARY)
  doc.text(label, margin + 8, y + 12)
  return y + h
}

// ── Containers table ────────────────────────────────────────────────

function drawContainersTable(
  doc: jsPDF,
  rapport: EmpotageRapport,
  t: TFunction<"transit">,
  margin: number,
  y: number,
): number {
  autoTable(doc, {
    startY: y,
    margin: { left: margin, right: margin },
    head: [
      [
        t("empotage.rapportPdf.cols.container"),
        t("empotage.rapportPdf.cols.plomb"),
        t("empotage.rapportPdf.cols.date"),
        t("empotage.rapportPdf.cols.loaded"),
        t("empotage.rapportPdf.cols.empty"),
        t("empotage.rapportPdf.cols.dressing"),
        t("empotage.rapportPdf.cols.theoreticalTare"),
        t("empotage.rapportPdf.cols.realTare"),
        t("empotage.rapportPdf.cols.netTheoretical"),
        t("empotage.rapportPdf.cols.netReal"),
      ],
    ],
    body: rapport.containers.map((c) => [
      c.containerNumber || "—",
      c.plombNumber || "—",
      c.stuffingDate ?? "—",
      kg(c.loadedKg),
      kg(c.emptyKg),
      kg(c.dressingKg),
      kg(c.theoreticalTareKg),
      kg(c.realTareKg),
      kg(c.netTheoreticalKg),
      kg(c.netRealKg),
    ]),
    foot: rapport.containers.length
      ? [
          [
            {
              content: t("empotage.rapportPdf.total"),
              colSpan: 8,
              styles: {
                halign: "left" as const,
                fillColor: TOTAL_BAND,
                fontStyle: "bold" as const,
              },
            },
            {
              content: kg(rapport.rollups.totalNetTheoreticalKg),
              styles: {
                halign: "right" as const,
                fillColor: TOTAL_BAND,
                fontStyle: "bold" as const,
              },
            },
            {
              content: kg(rapport.rollups.totalNetRealKg),
              styles: {
                halign: "right" as const,
                fillColor: TOTAL_BAND,
                fontStyle: "bold" as const,
              },
            },
          ],
        ]
      : [],
    styles: {
      fontSize: 8.5,
      cellPadding: 4,
      lineColor: BORDER,
      lineWidth: 0.5,
      textColor: TEXT_PRIMARY,
    },
    headStyles: {
      fillColor: HEADER_BAND,
      textColor: TEXT_PRIMARY,
      fontStyle: "bold" as const,
    },
    columnStyles: {
      3: { halign: "right" as const },
      4: { halign: "right" as const },
      5: { halign: "right" as const },
      6: { halign: "right" as const },
      7: { halign: "right" as const },
      8: { halign: "right" as const },
    },
  })
  return finalY(doc)
}

// ── Rollups table ───────────────────────────────────────────────────

function drawRollupsTable(
  doc: jsPDF,
  rapport: EmpotageRapport,
  t: TFunction<"transit">,
  margin: number,
  y: number,
): number {
  const { rollups } = rapport
  autoTable(doc, {
    startY: y,
    margin: { left: margin, right: margin },
    head: [
      [
        t("empotage.rollups.engage"),
        t("empotage.rollups.totalNetTheoretical"),
        t("empotage.rollups.totalNetReal"),
        t("empotage.rollups.ecartTheoretical"),
        t("empotage.rollups.ecartReal"),
        t("empotage.rapportPdf.freinteTheoretical"),
        t("empotage.rapportPdf.freinteReal"),
      ],
    ],
    body: [
      [
        kg(rollups.engageKg),
        kg(rollups.totalNetTheoreticalKg),
        kg(rollups.totalNetRealKg),
        rollups.theoretical ? signedKg(rollups.theoretical.ecart) : "—",
        rollups.real ? signedKg(rollups.real.ecart) : "—",
        rollups.theoretical ? pct(rollups.theoretical.freintePct) : "—",
        rollups.real ? pct(rollups.real.freintePct) : "—",
      ],
    ],
    styles: {
      fontSize: 9,
      cellPadding: 6,
      halign: "center" as const,
      lineColor: BORDER,
      lineWidth: 0.5,
      textColor: TEXT_PRIMARY,
    },
    headStyles: {
      fillColor: HEADER_BAND,
      textColor: TEXT_PRIMARY,
      fontStyle: "bold" as const,
      fontSize: 8,
      halign: "center" as const,
    },
    bodyStyles: { fontStyle: "bold" as const },
  })
  return finalY(doc)
}

// ── Observations table ──────────────────────────────────────────────

function drawObservationsTable(
  doc: jsPDF,
  rows: RapportContainerRow[],
  t: TFunction<"transit">,
  margin: number,
  y: number,
): number {
  autoTable(doc, {
    startY: y,
    margin: { left: margin, right: margin },
    head: [
      [
        t("empotage.rapportPdf.cols.container"),
        t("empotage.observation"),
      ],
    ],
    body: rows.map((c) => [c.containerNumber || `#${c.id}`, c.observation ?? ""]),
    styles: {
      fontSize: 9,
      cellPadding: 5,
      lineColor: BORDER,
      lineWidth: 0.5,
      textColor: TEXT_PRIMARY,
    },
    headStyles: {
      fillColor: HEADER_BAND,
      textColor: TEXT_PRIMARY,
      fontStyle: "bold" as const,
    },
    columnStyles: {
      0: { cellWidth: 110, halign: "left" as const },
      1: { halign: "left" as const },
    },
  })
  return finalY(doc)
}

// ── Photo embedding ─────────────────────────────────────────────────

function drawPhoto(doc: jsPDF, dataUrl: string, margin: number) {
  const pageWidth = doc.internal.pageSize.getWidth()
  const pageHeight = doc.internal.pageSize.getHeight()
  const maxW = pageWidth - margin * 2
  // Leave room for the title at the top and the footer at the bottom.
  const top = margin + 38
  const maxH = pageHeight - top - margin - 24
  // We don't know intrinsic dimensions here; let jsPDF scale to the
  // bounding box. addImage with explicit width/height will stretch —
  // that's the trade-off for not parsing the image client-side. The
  // resulting box is uniform regardless of the source aspect ratio.
  try {
    doc.addImage(dataUrl, "JPEG", margin, top, maxW, maxH, undefined, "FAST")
  } catch {
    // Some image formats (HEIC, WebP) aren't jsPDF-renderable. Drop
    // the page back to a "could not embed" note rather than throwing.
    doc.setFontSize(10)
    doc.setTextColor(TEXT_MUTED)
    doc.text("Image could not be embedded.", pageWidth / 2, top + 20, {
      align: "center",
    })
    doc.setTextColor(TEXT_PRIMARY)
  }
}

async function fetchPhotoAsDataURL(
  otId: number | string,
  storedKey: string,
): Promise<string | null> {
  const { url } = await getDownloadUrl(otId, storedKey)
  const res = await fetch(url)
  if (!res.ok) return null
  const blob = await res.blob()
  return await blobToDataURL(blob)
}

function blobToDataURL(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(blob)
  })
}

// ── Footer + helpers ────────────────────────────────────────────────

function drawFooter(
  doc: jsPDF,
  page: number,
  total: number,
  t: TFunction<"transit">,
) {
  const pageHeight = doc.internal.pageSize.getHeight()
  const pageWidth = doc.internal.pageSize.getWidth()
  doc.setFontSize(8)
  doc.setFont("helvetica", "normal")
  doc.setTextColor(TEXT_MUTED)
  doc.text(
    t("empotage.rapportPdf.pageFooter", {
      page,
      total,
      defaultValue: "Page {{page}} / {{total}}",
    }),
    pageWidth / 2,
    pageHeight - 16,
    { align: "center" },
  )
  doc.setTextColor(TEXT_PRIMARY)
}

function finalY(doc: jsPDF): number {
  return (doc as unknown as { lastAutoTable: { finalY: number } })
    .lastAutoTable.finalY
}

function kg(v: number | null | undefined): string {
  return v == null ? "—" : `${Math.round(v).toLocaleString()} kg`
}

function signedKg(v: number): string {
  if (v === 0) return "0 kg"
  const sign = v > 0 ? "+" : "−"
  return `${sign}${Math.abs(Math.round(v)).toLocaleString()} kg`
}

function pct(v: number): string {
  return `${v.toFixed(3)} %`
}
